// Ported from dev-scrips-pm-react src/documents/lib/file-allowlist.ts (PROD-1119 documents module).
/**
 * Documents-module upload guards — file-extension allowlist + size check.
 *
 * PROD-901 + PROD-902 (HIPAA-adjacent). Mirrors Flutter
 * `scrips_shared_features/lib/features/document_module/presentation/
 *  widgets/pick_document_widget.dart` — both the `accept` allowlist
 * and the pre-upload size gate. Toast strings copy Flutter verbatim;
 * deviating would surface in side-by-side QA.
 *
 * The constants are Flutter-locked and were already ported once by the
 * billing module's ClaimObservation File-variant work (PR #271 →
 * `src/billing/lib/billing-constants.ts`). To keep a single source of
 * truth without modifying billing, this file re-exports the canonical
 * billing names under documents-friendly identifiers plus two helpers
 * for the popup's call site. No drive-by refactor of billing.
 *
 * Flutter source references:
 * - `pick_document_widget.dart:212-223` — Browse picker allowlist
 *   (`['PDF', 'RTF', 'JPEG', 'JPG', 'PNG', 'CSV', 'XLS', 'XLSX',
 *     'DOC', 'DOCX']`)
 * - `pick_document_widget.dart:297-308` — `isValidFileType` —
 *   lowercases + chained `.endsWith('.pdf')`/etc.
 * - `pick_document_widget.dart:145, :237` — verbatim oversize toast
 *   `'File size should be less than or equal to ${limit}MB'`
 * - `pick_document_widget.dart:177, :269` — verbatim bad-ext toast
 *   `'Upload only PDF, RTF, JPEG, JPG, PNG, CSV, XLS, XLSX, DOC &
 *    DOCX file'`
 * - `scrips_core/utils/file_picker.dart:51-57` — size check with 5%
 *   buffer (`sizeInMb > limit * 1.05`).
 * - `login_user_data_model.dart:154-156, :200` — default `10` MB when
 *   the login response omits `maxDocumentUploadLimit`.
 */

import { isFileSizeGreater } from '@/billing/lib/file-size';
import {
  DEFAULT_MAX_DOCUMENT_UPLOAD_LIMIT_MB,
  OBSERVATION_FILE_EXTENSIONS,
  TOAST_FILE_INVALID,
} from '@/billing/lib/billing-constants';

/** Allowlist of file extensions (lowercase, with leading dot). */
export const DOCUMENT_FILE_EXTENSIONS = OBSERVATION_FILE_EXTENSIONS;

/** Verbatim Flutter toast for an extension-allowlist rejection. */
export const TOAST_DOCUMENT_FILE_INVALID = TOAST_FILE_INVALID;

/** Default size limit (MB) when auth-store has no per-org override. */
export const DEFAULT_DOCUMENT_MAX_UPLOAD_MB = DEFAULT_MAX_DOCUMENT_UPLOAD_LIMIT_MB;

export { isFileSizeGreater };

/**
 * Comma-separated allowlist suitable for an `<input type="file" accept>`
 * attribute (e.g. `'.pdf,.rtf,.jpeg,.jpg,.png,.csv,.xls,.xlsx,.doc,.docx'`).
 */
export const DOCUMENT_ACCEPT_ATTR = DOCUMENT_FILE_EXTENSIONS.join(',');

/**
 * Mirror of Flutter `isValidFileType` (`pick_document_widget.dart:297`).
 * Lowercases the filename and matches any allowlisted extension via
 * `endsWith`. Files without an extension always fail.
 */
export function isValidExtension(fileName: string): boolean {
  const lowered = fileName.toLowerCase();
  return DOCUMENT_FILE_EXTENSIONS.some((ext) => lowered.endsWith(ext));
}

/**
 * Per-extension expected MIME types (PR #339 Samer review item 3 —
 * defense against extension spoofing).
 *
 * The `<input accept>` attribute can be bypassed in any browser and
 * `isValidExtension` only checks the suffix. A file with malicious
 * content renamed `evil.pdf` will pass the extension check; but the
 * browser typically sniffs content and reports a `File.type` MIME
 * that disagrees with the extension. This map lets the popup reject
 * files whose `File.type` is **populated** but **doesn't match** the
 * extension's expected MIME set.
 *
 * ## Permissive fallback paths — INTENTIONAL, with observability
 *
 * Two cases pass the check despite being trivially producible by a
 * malicious or misconfigured client. Both are kept permissive
 * because rejecting them would break legitimate use cases the
 * average user can't fix; both emit a `console.warn` so the rate
 * is observable in ops / Sentry:
 *
 * 1. **Empty `File.type`** — Older Safari / Firefox builds, some
 *    Linux file managers (Nautilus on certain MIME-db configs),
 *    files copied from Windows network shares, and FormData built
 *    from `Blob` objects in some test environments all surface
 *    empty `type`. Rejecting empty would reject a meaningful
 *    fraction of legitimate uploads. Flutter `pick_document_widget`
 *    doesn't sniff MIME at all (extension-only) — empty-passes is
 *    already strictly stricter than Flutter parity.
 *
 * 2. **`application/octet-stream`** — Browser-default when the
 *    server-side type-db doesn't recognise the extension OR the
 *    file came from a binary download (e.g. lab vendor portals
 *    routinely serve PDF reports as `octet-stream`). Rejecting
 *    this would reject a meaningful chunk of legitimate
 *    healthcare-attached files. The defense-in-depth gate stays at
 *    the .NET `UploadController` (PROD-938) which can magic-byte
 *    sniff if/when it tightens. We keep this permissive AND log
 *    the rate so a backend tightening can be paired with a
 *    React-side tightening informed by real data.
 *
 * The strict case — populated MIME that disagrees with the
 * extension (e.g. `evil.pdf` with `application/x-msdownload`) —
 * is rejected. That catches the typical attack: Windows browsers
 * sniff PE headers and surface `application/x-msdownload`, and
 * trying-to-spoof clients usually set the wrong literal.
 */
const OCTET = 'application/octet-stream';
const ALLOWED_MIME_BY_EXTENSION: Record<string, ReadonlyArray<string>> = {
  '.pdf': ['application/pdf', OCTET],
  '.rtf': ['application/rtf', 'text/rtf', OCTET],
  '.jpeg': ['image/jpeg', OCTET],
  '.jpg': ['image/jpeg', OCTET],
  '.png': ['image/png', OCTET],
  '.csv': ['text/csv', 'application/csv', 'application/vnd.ms-excel', OCTET],
  '.xls': ['application/vnd.ms-excel', OCTET],
  '.xlsx': [
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-excel',
    OCTET,
  ],
  '.doc': ['application/msword', OCTET],
  '.docx': [
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/msword',
    OCTET,
  ],
};

/**
 * MIME-type secondary check (PR #339 Samer review item 3). Returns
 * `true` when EITHER (a) the file's reported MIME matches the
 * expected set for the extension, OR (b) the file's MIME is empty
 * (browser couldn't determine), OR (c) the file's MIME is
 * `application/octet-stream` (browser-default for unknown content).
 *
 * The two permissive cases (b) and (c) emit a `console.warn` so the
 * rate is observable. See the `ALLOWED_MIME_BY_EXTENSION` doc block
 * above for the explicit justification on why these are permissive
 * (Samer PR #339 follow-up review).
 *
 * Returns `false` only when the browser reports a populated MIME
 * that disagrees with the extension's allowlist — i.e. extension
 * spoofing is the most likely explanation.
 */
export function isMimeConsistent(file: File): boolean {
  const lowered = file.name.toLowerCase();
  const ext = DOCUMENT_FILE_EXTENSIONS.find((e) => lowered.endsWith(e));
  if (!ext) return false;
  if (!file.type) {
    // eslint-disable-next-line no-console
    console.warn(
      `[file-allowlist] permissive accept: empty File.type on "${file.name}". ` +
        'See ALLOWED_MIME_BY_EXTENSION justification in file-allowlist.ts.',
    );
    return true;
  }
  if (file.type.toLowerCase() === OCTET) {
    // eslint-disable-next-line no-console
    console.warn(
      `[file-allowlist] permissive accept: application/octet-stream on "${file.name}". ` +
        'See ALLOWED_MIME_BY_EXTENSION justification in file-allowlist.ts.',
    );
    return true;
  }
  const allowed = ALLOWED_MIME_BY_EXTENSION[ext];
  if (!allowed) return false;
  return allowed.includes(file.type.toLowerCase());
}

/**
 * Verbatim Flutter oversize toast template. The interpolation has NO
 * space between the limit and "MB" (Flutter literal:
 * `'File size should be less than or equal to ${limit}MB'`).
 */
export function oversizeMessage(limitMb: number): string {
  return `File size should be less than or equal to ${limitMb}MB`;
}

/**
 * Toast surfaced when `maxDocumentUploadLimit` is missing on the
 * auth-store user (PR #339 Samer review item 1). Bad config should be
 * loud, not silently permissive — Samer explicitly called this out as
 * a HIPAA concern. Shipped alongside an inline-warning banner so the
 * user can see the org has a misconfig before they upload PHI.
 */
export const TOAST_UPLOAD_LIMIT_MISSING =
  "Your organisation's upload limit isn't configured. Contact support before uploading patient files.";
